import React, { createContext, useContext, forwardRef } from "react";
import Box, { BoxProps } from "../box/Box";

interface IFormControl {
  /**
   * Content of the form control.
   */
  children?: React.ReactNode;
  /**
   * If `true` set the form control to the invalid state.
   */
  invalid?: boolean;
  /**
   * If `true` set the form control to be required.
   */
  required?: boolean;
  /**
   * If `true` set the form control to the disabled state.
   */
  disabled?: boolean;
  /**
   * If `true` set the form control to the read only state.
   */
  readOnly?: boolean;
}

export type FormControlProps = IFormControl & BoxProps;

/**
 * FormControl provides context such as `invalid`, `required`, `disabled` to it's children.
 */
const FormControl: React.FC<FormControlProps> = forwardRef(
  ({ invalid = false, required = false, disabled = false, readOnly = false, ...rest }, ref) => {
    const context = {
      required,
      disabled,
      invalid,
      readOnly,
    };

    return (
      <FormControlContext.Provider value={context}>
        <Box role="group" ref={ref} {...rest} />
      </FormControlContext.Provider>
    );
  },
);

FormControl.displayName = "FormControl";

export default FormControl;

export const useFormControl = (props: any) => {
  const context = useFormControlContext();

  if (!context) {
    return props;
  }

  const keys = Object.keys(context);

  return keys.reduce((acc: any, prop: any) => {
    acc[prop] = props[prop];

    if (context) {
      if (props[prop] == null) {
        acc[prop] = (context as any)[prop];
      }
    }

    return acc;
  }, {});
};

const FormControlContext = createContext({ required: false, disabled: false, invalid: false, readOnly: false });

export const useFormControlContext = () => {
  const context = useContext(FormControlContext);
  return context;
};
